import { useMemo, useRef, useState } from 'react'
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded'
import { FloatingMenu } from './FloatingMenu'
import { OverflowTip } from './Hover/OverflowTip'
import { Button } from './Button'
import { Pressable } from './Pressable'
import './meetingWhenPicker.css'

/**
 * Meeting one-day when picker — a single trigger that opens ONE popover
 * containing a mini month grid, a start-time input, and a duration
 * dropdown. Emits a single ISO `datetime-local` string + duration minutes,
 * matching the fields the meeting draft already uses.
 *
 * Timezone is intentionally not shown — meetings are always local.
 */

type Props = {
  /** Local datetime-local string: `YYYY-MM-DDTHH:mm` */
  startInput: string
  durationMinutes: number
  onChange: (next: { startInput: string; durationMinutes: number }) => void
  disabled?: boolean
  ariaLabel?: string
  className?: string
}

const WEEKDAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
] as const
const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
] as const
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

const DURATION_PRESETS = [15, 30, 45, 60, 90, 120, 180] as const

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n))

const parseInput = (raw: string): { date: Date; hours: number; minutes: number } => {
  const [date, time] = raw.split('T')
  const [y, m, d] = date.split('-').map(Number)
  const [h, mm] = (time ?? '00:00').split(':').map(Number)
  return {
    date: new Date(y, (m ?? 1) - 1, d ?? 1),
    hours: h ?? 0,
    minutes: mm ?? 0,
  }
}

const toLocalInput = (date: Date, hours: number, minutes: number) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(hours)}:${pad2(minutes)}`

const addDaysLocal = (d: Date, delta: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta)

const addMonths = (d: Date, delta: number) =>
  new Date(d.getFullYear(), d.getMonth() + delta, 1)

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1)

const monthMatrix = (monthStart: Date): Date[] => {
  const first = new Date(monthStart)
  const firstDow = first.getDay()
  const gridStart = addDaysLocal(first, -firstDow)
  const cells: Date[] = []
  for (let i = 0; i < 42; i += 1) cells.push(addDaysLocal(gridStart, i))
  return cells
}

const formatDurationChip = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const rem = minutes % 60
  return rem ? `${hours}h ${rem}m` : `${hours}h`
}

const formatTriggerLabel = (startInput: string, durationMinutes: number): string => {
  const parsed = parseInput(startInput)
  const day = parsed.date
  const endMinutes = parsed.hours * 60 + parsed.minutes + durationMinutes
  const endHours = Math.floor(endMinutes / 60) % 24
  const endMinutesOfHour = endMinutes % 60
  return `${WEEKDAY_SHORT[day.getDay()]} ${day.getDate()} ${MONTH_NAMES_SHORT[day.getMonth()]} · ${pad2(parsed.hours)}:${pad2(parsed.minutes)}–${pad2(endHours)}:${pad2(endMinutesOfHour)}`
}

export const MeetingWhenPicker = ({
  startInput,
  durationMinutes,
  onChange,
  disabled = false,
  ariaLabel = 'When',
  className,
}: Props) => {
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const [open, setOpen] = useState(false)
  const parsed = useMemo(() => parseInput(startInput), [startInput])
  const [monthCursor, setMonthCursor] = useState<Date>(() => startOfMonth(parsed.date))

  const cells = useMemo(() => monthMatrix(monthCursor), [monthCursor])
  const selectedDate = parsed.date
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const commitDate = (next: Date) => {
    onChange({
      startInput: toLocalInput(next, parsed.hours, parsed.minutes),
      durationMinutes,
    })
  }
  const commitTime = (hoursMinutes: string) => {
    const [h, m] = hoursMinutes.split(':').map(Number)
    onChange({
      startInput: toLocalInput(selectedDate, h ?? 0, m ?? 0),
      durationMinutes,
    })
  }
  const commitDuration = (minutes: number) => {
    onChange({ startInput, durationMinutes: minutes })
  }

  return (
    <div className={`meetingWhenPicker ${className ?? ''}`.trim()}>
      <Pressable
        ref={anchorRef}
        className={`meetingWhenPickerTrigger${open ? ' isOpen' : ''}`}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <CalendarTodayRoundedIcon fontSize="small" />
        <OverflowTip label={formatTriggerLabel(startInput, durationMinutes)}>
          <span className="meetingWhenPickerLabel">{formatTriggerLabel(startInput, durationMinutes)}</span>
        </OverflowTip>
        <span className="meetingWhenPickerDurChip">{formatDurationChip(durationMinutes)}</span>
      </Pressable>
      <FloatingMenu
        anchorRef={anchorRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        placement="bottom-start"
        className="meetingWhenPickerMenu"
        role="dialog"
        ariaLabel="Pick meeting date and time"
      >
        <div className="mwpBody">
          <div className="mwpCalendar">
            <div className="mwpCalendarNav">
              <Pressable onClick={() => setMonthCursor((c) => addMonths(c, -1))} aria-label="Previous month">‹</Pressable>
              <span>{MONTH_NAMES[monthCursor.getMonth()]} {monthCursor.getFullYear()}</span>
              <Pressable onClick={() => setMonthCursor((c) => addMonths(c, 1))} aria-label="Next month">›</Pressable>
            </div>
            <div className="mwpCalendarWeekdays">
              {WEEKDAY_LABELS.map((label, index) => (
                <span key={`${label}-${index}`}>{label}</span>
              ))}
            </div>
            <div className="mwpCalendarGrid">
              {cells.map((cell) => {
                const outOfMonth = cell.getMonth() !== monthCursor.getMonth()
                const isSelected = cell.toDateString() === selectedDate.toDateString()
                const isToday = cell.getTime() === today.getTime()
                return (
                  <Pressable
                    key={cell.toISOString()}
                    className={`mwpDay${outOfMonth ? ' isOutside' : ''}${isSelected ? ' isSelected' : ''}${isToday ? ' isToday' : ''}`}
                    onClick={() => commitDate(cell)}
                  >
                    {cell.getDate()}
                  </Pressable>
                )
              })}
            </div>
          </div>
          <div className="mwpRight">
            <label className="mwpField">
              <span>Start</span>
              <input
                type="time"
                value={`${pad2(parsed.hours)}:${pad2(parsed.minutes)}`}
                onChange={(event) => commitTime(event.target.value)}
                step={300}
              />
            </label>
            <div className="mwpField">
              <span>Duration</span>
              <div className="mwpDurationChips">
                {DURATION_PRESETS.map((minutes) => (
                  <Button
                    key={minutes}
                    variant="tertiary"
                    size="sm"
                    pressed={minutes === durationMinutes}
                    onClick={() => commitDuration(minutes)}
                  >
                    {formatDurationChip(minutes)}
                  </Button>
                ))}
              </div>
              <input
                type="number"
                min={5}
                max={12 * 60}
                step={5}
                value={durationMinutes}
                onChange={(event) => {
                  const parsedValue = Number(event.target.value)
                  if (Number.isFinite(parsedValue) && parsedValue >= 5) commitDuration(Math.min(12 * 60, Math.round(parsedValue)))
                }}
                aria-label="Duration in minutes"
                className="mwpDurationCustom"
              />
            </div>
            <div className="mwpFooter">
              <Button variant="primary" size="sm" onClick={() => setOpen(false)}>Done</Button>
            </div>
          </div>
        </div>
      </FloatingMenu>
    </div>
  )
}
